import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  DASHBOARD_FETCH_WAVE_TIMEOUT_MS,
  initialDashboardFetchWaveState,
  isDashboardFetchWavePending,
  stepDashboardFetchWave,
  timeoutDashboardFetchWave,
  type DashboardFetchWaveState,
} from './dashboardFetchWave'

type FetchWaveReport = { wave: number; loading: boolean }

type DashboardFetchWaveApi = {
  released: number
  register: (id: number, wave: number, loading: boolean) => void
  unregister: (id: number) => void
}

const DashboardFetchWaveContext = createContext<DashboardFetchWaveApi | null>(
  null,
)

let nextFetchWaveReportId = 1

export function DashboardFetchWaveProvider({
  resetKey,
  waveCount,
  children,
}: {
  /** Tab, date, range, or huddle day. Interval refresh must not change this. */
  resetKey: string
  waveCount: number
  children: React.ReactNode
}): React.ReactElement {
  const [state, setState] = useState<DashboardFetchWaveState>(
    initialDashboardFetchWaveState,
  )
  const [reportVersion, setReportVersion] = useState(0)
  const reportsRef = useRef<Map<number, FetchWaveReport>>(new Map())
  // State, not a ref: StrictMode renders twice and a ref update would hide the
  // reset on the second pass, leaving every wave open for the new date.
  const [trackedKey, setTrackedKey] = useState(resetKey)
  let viewState = state
  if (trackedKey !== resetKey) {
    setTrackedKey(resetKey)
    reportsRef.current.clear()
    viewState = initialDashboardFetchWaveState()
    setState(viewState)
  }
  const register = useRef<DashboardFetchWaveApi['register']>(() => undefined)
  const unregister = useRef<DashboardFetchWaveApi['unregister']>(
    () => undefined,
  )

  register.current = (id, wave, loading) => {
    const prev = reportsRef.current.get(id)
    if (prev && prev.wave === wave && prev.loading === loading) return
    reportsRef.current.set(id, { wave, loading })
    setReportVersion((version) => version + 1)
  }
  unregister.current = (id) => {
    if (!reportsRef.current.has(id)) return
    reportsRef.current.delete(id)
    setReportVersion((version) => version + 1)
  }

  const api = useMemo<DashboardFetchWaveApi>(
    () => ({
      released: viewState.released,
      register: (id, wave, loading) => register.current(id, wave, loading),
      unregister: (id) => unregister.current(id),
    }),
    [viewState.released],
  )

  useEffect(() => {
    const reports: { wave: number; loading: boolean }[] = []
    reportsRef.current.forEach((report) => {
      reports.push(report)
    })
    const pending = isDashboardFetchWavePending(reports, viewState.released)
    const next = stepDashboardFetchWave(viewState, pending, waveCount)
    if (
      next.released === viewState.released &&
      next.phase === viewState.phase
    ) {
      return
    }
    setState(next)
  }, [viewState, reportVersion, waveCount])

  useEffect(() => {
    if (viewState.released >= waveCount - 1) return
    const waveAtStart = viewState.released
    const id = window.setTimeout(() => {
      setState((current) =>
        current.released === waveAtStart
          ? timeoutDashboardFetchWave(current, waveCount)
          : current,
      )
    }, DASHBOARD_FETCH_WAVE_TIMEOUT_MS)
    return () => window.clearTimeout(id)
  }, [viewState.released, waveCount])

  return (
    <DashboardFetchWaveContext.Provider value={api}>
      {children}
    </DashboardFetchWaveContext.Provider>
  )
}

/** True when this wave may fetch. Outside the provider every wave may fetch. */
export function useDashboardFetchWave(wave: number): boolean {
  const api = useContext(DashboardFetchWaveContext)
  if (!api) return true
  return api.released >= wave
}

/** Release flags for waves 0..2. Call unconditionally. */
export function useDashboardFetchWaves(): readonly [boolean, boolean, boolean] {
  return [
    useDashboardFetchWave(0),
    useDashboardFetchWave(1),
    useDashboardFetchWave(2),
  ]
}

export function isFetchWaveOpen(
  open: readonly boolean[],
  wave: number | null,
): boolean {
  if (wave == null) return false
  return open[wave] === true
}

export type DashboardFetchGate = {
  enabled: boolean
  /** Visible section whose wave is not open yet. */
  queued: boolean
}

/** Hidden when `wave` is null. Otherwise queued until that wave is released. */
export function waveFetchState(
  open: readonly boolean[],
  wave: number | null,
): DashboardFetchGate {
  if (wave == null) return { enabled: false, queued: false }
  const enabled = open[wave] === true
  return { enabled, queued: !enabled }
}

/**
 * Fetch gate for a self-fetching card. Outside the provider every wave may
 * fetch immediately, so nothing is queued.
 */
export function useDashboardFetchGate(wave: number): DashboardFetchGate {
  const api = useContext(DashboardFetchWaveContext)
  if (!api) return { enabled: true, queued: false }
  const enabled = api.released >= wave
  return { enabled, queued: !enabled }
}

/**
 * Registers in-flight work for a wave. `pending` should be false until the
 * wave is enabled and its request is loading.
 */
export function useReportDashboardFetchWave(
  wave: number,
  pending: boolean,
): void {
  const api = useContext(DashboardFetchWaveContext)
  const idRef = useRef(0)
  if (idRef.current === 0) idRef.current = nextFetchWaveReportId++
  const waveEnabled = !api || api.released >= wave

  useEffect(() => {
    if (!api) return
    if (!waveEnabled) {
      api.unregister(idRef.current)
      return
    }
    api.register(idRef.current, wave, pending)
    return () => api.unregister(idRef.current)
  }, [api, pending, wave, waveEnabled])
}
